import api from './client';

// Enums
export enum PurchaseStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED'
}

export enum PaymentMethod {
  CASH = 'CASH',
  CREDIT_CARD = 'CREDIT_CARD',
  BANK_TRANSFER = 'BANK_TRANSFER',
  CHECK = 'CHECK',
  MOBILE_PAYMENT = 'MOBILE_PAYMENT',
  OTHER = 'OTHER',
}

export enum PurchaseItemType {
  BIRD = 'BIRD',
  SUPPLY = 'SUPPLY',
  EQUIPMENT = 'EQUIPMENT',
  SERVICE = 'SERVICE',
  FEED = 'FEED',
  OTHER = 'OTHER',
}

// Interfaces
export interface PurchaseItem {
  id?: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  itemType?: PurchaseItemType;
  itemReferenceId?: string;
  notes?: string;
}

export interface Purchase {
  id: string;
  invoiceNumber: string;
  purchaseDate: string;
  supplierName: string;
  supplierPhone: string;
  supplierAddress: string;
  paymentMethod: PaymentMethod;
  total: number;
  status: PurchaseStatus;
  notes?: string;
  items: PurchaseItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatePurchaseDto {
  invoiceNumber: string;
  purchaseDate: string;
  supplierName: string;
  supplierPhone: string;
  supplierAddress: string;
  paymentMethod: PaymentMethod;
  total: number;
  notes?: string;
  items: Omit<PurchaseItem, 'id'>[];
}

export interface UpdatePurchaseDto {
  invoiceNumber: string;
  purchaseDate: string;
  supplierName: string;
  supplierPhone: string;
  supplierAddress: string;
  paymentMethod: PaymentMethod;
  total: number;
  notes?: string;
  items: Omit<PurchaseItem, 'id'>[];
  status: PurchaseStatus;
}

export interface GetPurchasesDto {
  supplier?: string;
  status?: PurchaseStatus;
  paymentMethod?: PaymentMethod;
  itemType?: PurchaseItemType;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'ASC' | 'DESC';
}

export interface PurchasesAnalytics {
  totalPurchasesAmount: number;
  totalPurchasesCount: number;
  purchasesAmountByStatus: Record<string, number>;
  purchasesCountByStatus: Record<string, number>;
}

export interface PurchasesByStatusAnalytics {
  totalPurchases: number;
  purchasesByStatus: Record<string, number>;
  purchasesCount: number;
}

// API functions
export const purchasesApi = {
  // Get all purchases
  async getPurchases(filters: GetPurchasesDto = {}): Promise<Purchase[]> {
    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const queryString = queryParams.toString();
    const url = `accounting/purchases${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get<Purchase[]>(url);
    return response.data;
  },

  // Get purchase by ID
  async getPurchase(id: string): Promise<Purchase> {
    const response = await api.get<Purchase>(`accounting/purchases/${id}`);
    return response.data;
  },

  // Create purchase
  async createPurchase(data: CreatePurchaseDto): Promise<Purchase> {
    const response = await api.post<Purchase>('accounting/purchases', data);
    return response.data;
  },

  // Update purchase
  async updatePurchase(id: string, data: UpdatePurchaseDto): Promise<Purchase> {
    const response = await api.put<Purchase>(`accounting/purchases/${id}`, data);
    return response.data;
  },

  // Delete purchase
  async deletePurchase(id: string): Promise<void> {
    await api.delete(`accounting/purchases/${id}`);
  },

  // Get purchases analytics
  async getPurchasesAnalytics(): Promise<PurchasesAnalytics> {
    const response = await api.get<PurchasesAnalytics>('accounting/purchases/analytics');
    return response.data;
  },

  // Get purchases by status
  async getPurchasesByStatus(): Promise<PurchasesByStatusAnalytics> {
    const response = await api.get<PurchasesByStatusAnalytics>('accounting/purchases/status');
    return response.data;
  },

  // Get total purchases
  async getTotalPurchases(): Promise<{ total: number }> {
    const response = await api.get<{ total: number }>('accounting/purchases/total');
    return response.data;
  }
};

// Helper functions
export const formatCurrency = (amount: number, currency = 'ZAR'): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

export const getPurchaseStatusColor = (status: PurchaseStatus): string => {
  switch (status) {
    case PurchaseStatus.PENDING:
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case PurchaseStatus.COMPLETED:
      return 'bg-green-100 text-green-800 border-green-200';
    case PurchaseStatus.CANCELLED:
      return 'bg-red-100 text-red-800 border-red-200';
    case PurchaseStatus.REFUNDED:
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const getPaymentMethodDisplay = (method: PaymentMethod): string => {
  switch (method) {
    case PaymentMethod.CASH:
      return 'Cash';
    case PaymentMethod.CREDIT_CARD:
      return 'Credit Card';
    case PaymentMethod.BANK_TRANSFER:
      return 'Bank Transfer';
    case PaymentMethod.CHECK:
      return 'Check';
    case PaymentMethod.MOBILE_PAYMENT:
      return 'Mobile Payment';
    case PaymentMethod.OTHER:
      return 'Other';
    default:
      return method;
  }
};

export const getPurchaseItemTypeDisplay = (itemType: PurchaseItemType): string => {
  switch (itemType) {
    case PurchaseItemType.BIRD:
      return 'Bird';
    case PurchaseItemType.SUPPLY:
      return 'Supply';
    case PurchaseItemType.EQUIPMENT:
      return 'Equipment';
    case PurchaseItemType.SERVICE:
      return 'Service';
    case PurchaseItemType.FEED:
      return 'Feed';
    case PurchaseItemType.OTHER:
      return 'Other';
    default:
      return itemType || 'Other';
  }
};

export const getPurchaseItemTypeColor = (itemType: PurchaseItemType): string => {
  switch (itemType) {
    case PurchaseItemType.BIRD:
      return 'bg-green-100 text-green-800 border-green-200';
    case PurchaseItemType.SUPPLY:
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case PurchaseItemType.EQUIPMENT:
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case PurchaseItemType.SERVICE:
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case PurchaseItemType.FEED:
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case PurchaseItemType.OTHER:
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const calculatePurchaseTotal = (items: PurchaseItem[]): number => {
  return items.reduce((total, item) => total + item.totalPrice, 0);
};

export const generateInvoiceNumber = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const time = String(now.getTime()).slice(-4);
  return `PUR-${year}${month}${day}-${time}`;
}; 